import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { captureAnalytics } from '../../actions/preload';

const PREVIEWS = {
  referral_counts: {
    url: '/api/v1/referral_count_preview',
    readUrls: ['/api/v1/receivers/referral_status_counts'],
    headerPrefix: 'x-referral-counts',
    id: 'referral-count-preview',
    label: 'Referral counts',
    subject: 'counts',
    newMode: 'projection',
    newPath: 'projection',
    analytics: 'referral_count_preview_changed',
  },
  inbox_sql: {
    url: '/api/v1/inbox_sql_preview',
    readUrls: ['/api/v1/records', '/api/v1/receivers/count'],
    headerPrefix: 'x-inbox-sql',
    id: 'inbox-sql-preview',
    label: 'Inbox SQL',
    subject: 'query',
    newMode: 'optimized',
    newPath: 'optimized',
    analytics: 'inbox_sql_preview_changed',
  },
};

const ImpersonationQueryPreview = ({ kind }) => {
  const config = PREVIEWS[kind];
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    const interceptor = axios.interceptors.response.use((response) => {
      if (mounted.current && config.readUrls.includes(response.config?.url?.split('?')[0])) {
        const path = response.headers[`${config.headerPrefix}-path`];
        const fallback = response.headers[`${config.headerPrefix}-fallback`];
        const mode = response.headers[`${config.headerPrefix}-mode`];
        // Ignore unrelated Inbox count reads that carry no SQL preview diagnostics.
        if (!path && kind === 'inbox_sql') return response;
        setLastRead(path ? { path, fallback, mode } : null);
      }
      return response;
    });
    // One request per impersonation context, canceled when this banner unmounts.
    axios.get(config.url, { signal: controller.signal }).then(({ data }) => {
      if (mounted.current && !controller.signal.aborted) setSettings(data);
    }).catch(() => {
      if (mounted.current) setSettings(null);
    });
    return () => {
      mounted.current = false;
      controller.abort();
      axios.interceptors.response.eject(interceptor);
    };
  }, [config, kind]);

  const changeMode = async (event) => {
    const mode = event.target.value;
    setSaving(true);
    setError(false);
    try {
      await axios.patch(config.url, { mode, context: settings.context }, {
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      });
      if (!mounted.current) return;
      captureAnalytics(config.analytics, { mode });
      window.location.reload();
    } catch {
      if (mounted.current) {
        // Successful saves stay disabled until navigation; failed saves retry.
        setSaving(false);
        setError(true);
      }
    }
  };

  if (!settings) return null;

  let readLabel = kind === 'inbox_sql' ? 'Waiting for next query' : 'Waiting for counts';
  if (lastRead?.path === config.newPath) readLabel = `Last ${config.subject}: new`;
  if (lastRead?.path === 'legacy') {
    readLabel = lastRead.fallback && !['feature_disabled', 'preview_legacy', 'search_not_selected'].includes(lastRead.fallback)
      ? `Last ${config.subject}: old (fallback)` : `Last ${config.subject}: old`;
  }

  return (
    <span style={{
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    }}
    >
      <label htmlFor={config.id}>
        {config.label}
        :
        {' '}
        <select id={config.id} value={lastRead?.mode || settings.mode} onChange={changeMode} disabled={saving}>
          <option value="normal">Normal</option>
          <option value="legacy">Old</option>
          <option value={config.newMode}>New</option>
        </select>
      </label>
      <span style={{ fontSize: '12px' }} title={lastRead?.fallback || ''}>{saving ? 'Reloading…' : readLabel}</span>
      {error && <span role="alert">{`Couldn’t switch ${config.subject}. Reload and try again.`}</span>}
    </span>
  );
};

ImpersonationQueryPreview.propTypes = { kind: PropTypes.oneOf(Object.keys(PREVIEWS)) };
ImpersonationQueryPreview.defaultProps = { kind: 'referral_counts' };

export default ImpersonationQueryPreview;
